import { Nerdle } from '@minigame-race/contracts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MinigameProps } from '../../race/types.js';
import { color } from '../../theme.js';

/**
 * Nerdle.
 *
 * Wordle's loop over `0-9 + - * / =`. Same colour semantics: lime = right
 * symbol right spot, amber = right symbol wrong spot, grey = absent.
 *
 * No optimistic prediction, for the same reason as Wordle: feedback colours
 * can't be computed without the answer, and the server refuses to send it. The
 * row you're typing is local; everything above it is authoritative.
 */

/** Two rows: digits, then operators. Mirrors the real Nerdle keyboard. */
const DIGIT_ROW: readonly string[] = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const OP_ROW: readonly string[] = ['ENTER', '+', '-', '*', '/', '=', 'BACK'];

function isNerdleView(v: unknown): v is Nerdle.NerdlePrivateView {
  return typeof v === 'object' && v !== null && (v as { kind?: string }).kind === 'nerdle';
}

/** Best-known status per symbol, for keyboard tinting. correct > present > absent. */
function keyboardState(guesses: readonly Nerdle.ScoredEquation[]): Map<string, Nerdle.SymbolResult> {
  const rank: Record<Nerdle.SymbolResult, number> = { absent: 0, present: 1, correct: 2 };
  const map = new Map<string, Nerdle.SymbolResult>();
  for (const g of guesses) {
    for (let i = 0; i < g.equation.length; i++) {
      const ch = g.equation[i];
      const r = g.result[i];
      if (ch === undefined || r === undefined) continue;
      const prev = map.get(ch);
      if (prev === undefined || rank[r] > rank[prev]) map.set(ch, r);
    }
  }
  return map;
}

type TileState = Nerdle.SymbolResult | 'empty' | 'typing' | 'hint';

const tileColors = (r: TileState) => {
  switch (r) {
    case 'correct':
      return { background: `linear-gradient(160deg,${color.limeBright},${color.limeDeep})`, color: color.limeInk, border: 'none' };
    case 'present':
      return { background: 'linear-gradient(160deg,#ffc75a,#f0a000)', color: color.amberInk, border: 'none' };
    case 'absent':
      return { background: 'rgba(255,255,255,.045)', color: 'rgba(234,255,234,.35)', border: 'none' };
    case 'typing':
      return { background: 'rgba(166,255,46,.05)', color: color.text, border: `clamp(2px,.5cqmin,3px) solid rgba(166,255,46,.6)` };
    case 'hint':
      // A symbol you bought. Ghosted, so it can't be mistaken for one you deduced.
      return {
        background: 'rgba(166,255,46,.10)',
        color: 'rgba(198,255,94,.75)',
        border: `clamp(2px,.5cqmin,3px) dashed rgba(166,255,46,.55)`,
      };
    default:
      return { background: 'transparent', color: color.text, border: '2px solid rgba(234,255,234,.1)' };
  }
};

export function NerdleGame({ view, emit, rejectNonce }: MinigameProps) {
  const server = isNerdleView(view) ? view : null;
  const [draft, setDraft] = useState('');
  const [shake, setShake] = useState(0);

  const len = server?.equationLength ?? Nerdle.EQUATION_LENGTH;

  // A rejected guess (false equation, already tried) shakes the row and clears
  // it. Only clear when the draft was complete — a refused hint must not wipe
  // symbols the player has already typed.
  useEffect(() => {
    if (rejectNonce === 0) return;
    setShake((s) => s + 1);
    setDraft((d) => (d.length === len ? '' : d));
  }, [rejectNonce, len]);

  // Server accepted a guess: the row committed, so clear the draft.
  const guessCount = server?.guessesUsed ?? 0;
  useEffect(() => {
    setDraft('');
  }, [guessCount]);

  const submit = useCallback(() => {
    if (draft.length !== len) {
      setShake((s) => s + 1);
      return;
    }
    emit({ type: 'guess', equation: draft } satisfies Nerdle.NerdleGuessEvent);
  }, [draft, emit, len]);

  const press = useCallback(
    (key: string) => {
      if (server?.isComplete) return;
      if (key === 'ENTER') submit();
      else if (key === 'BACK') setDraft((d) => d.slice(0, -1));
      else if (/^[0-9+\-*/=]$/.test(key)) setDraft((d) => (d.length < len ? d + key : d));
    },
    [server?.isComplete, submit, len],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Enter') press('ENTER');
      else if (e.key === 'Backspace') press('BACK');
      // `x` for multiply is what people actually type on a numeric keyboard.
      else if (e.key === 'x' || e.key === 'X') press('*');
      else if (/^[0-9+\-*/=]$/.test(e.key)) press(e.key);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [press]);

  const kbState = useMemo(() => keyboardState(server?.guesses ?? []), [server?.guesses]);

  if (!server) {
    return <div style={{ color: color.textFaint, fontSize: 14 }}>Loading equation…</div>;
  }

  /** index -> revealed symbol, for cells the player has paid to see. */
  const hintAt = new Map(server.hints.map((h) => [h.index, h.symbol]));

  const rows = Array.from({ length: Nerdle.MAX_GUESSES }, (_, r) => {
    const done = server.guesses[r];
    if (done) return { symbols: [...done.equation], results: [...done.result] as TileState[] };

    if (r === server.guessesUsed && !server.isComplete) {
      const typed = draft.padEnd(len, ' ').split('');
      const symbols = typed.map((c, i) => (c.trim() ? c : (hintAt.get(i) ?? ' ')));
      const results = typed.map((c, i): TileState => {
        if (c.trim()) return 'typing';
        if (hintAt.has(i)) return 'hint';
        return 'empty';
      });
      return { symbols, results, current: true };
    }

    return {
      symbols: Array<string>(len).fill(' '),
      results: Array<TileState>(len).fill('empty'),
    };
  });

  return (
    <div style={{ width: 'min(74cqh,96cqw)', display: 'flex', flexDirection: 'column', gap: 'clamp(8px,1.8cqmin,18px)' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 'clamp(15px,3cqmin,27px)', letterSpacing: '.04em' }}>NERDLE</span>
          <span
            title={Nerdle.RULES_HINT}
            style={{
              fontSize: 'clamp(7px,1.3cqmin,11px)',
              letterSpacing: '.24em',
              fontWeight: 600,
              color: 'rgba(166,255,46,.6)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {Nerdle.RULES_NOTE}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(6px,1.2cqmin,10px)' }}>
          {server.hintsRemaining > 0 && !server.isComplete && (
            <button
              onPointerDown={(e) => {
                e.preventDefault();
                emit({ type: 'hint' } satisfies Nerdle.NerdleHintEvent);
              }}
              title="Reveals one symbol and its position"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'clamp(4px,.9cqmin,7px)',
                padding: 'clamp(4px,.9cqmin,8px) clamp(9px,1.8cqmin,15px)',
                border: '1px dashed rgba(255,176,32,.55)',
                borderRadius: 999,
                background: 'rgba(255,176,32,.08)',
                touchAction: 'manipulation',
              }}
            >
              <span style={{ fontSize: 'clamp(7px,1.3cqmin,11px)', letterSpacing: '.16em', fontWeight: 700, color: color.amberBright }}>
                💡 HINT
              </span>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 'clamp(10px,2.1cqmin,17px)', lineHeight: 1, color: color.amber }}>
                +{Math.round(server.hintPenaltyMs / 1000)}s
              </span>
            </button>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(5px,1.1cqmin,9px)',
              padding: 'clamp(4px,.9cqmin,8px) clamp(9px,1.8cqmin,16px)',
              border: '1px solid rgba(166,255,46,.4)',
              borderRadius: 999,
              background: 'rgba(166,255,46,.08)',
            }}
          >
            <span style={{ fontSize: 'clamp(7px,1.3cqmin,11px)', letterSpacing: '.2em', fontWeight: 600, color: 'rgba(166,255,46,.75)' }}>
              GUESS
            </span>
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 'clamp(14px,3cqmin,24px)', lineHeight: 1, color: color.limeBright }}>
              {server.guessesUsed}
              <span style={{ color: 'rgba(234,255,234,.4)', fontWeight: 600 }}>/{server.maxGuesses}</span>
            </span>
          </div>
        </div>
      </div>

      {/* grid — 8 columns, so it is wider and shorter than Wordle's */}
      <div
        style={{
          width: '100%',
          alignSelf: 'center',
          display: 'grid',
          gridTemplateRows: `repeat(${Nerdle.MAX_GUESSES},1fr)`,
          gap: 'clamp(3px,.8cqmin,7px)',
          aspectRatio: `${len} / ${Nerdle.MAX_GUESSES}`,
          maxHeight: '46cqh',
        }}
      >
        {rows.map((row, r) => (
          <div
            key={r}
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${len},1fr)`,
              gap: 'clamp(3px,.8cqmin,7px)',
              animation: row.current && shake > 0 ? `shake .3s ease-in-out ${shake}` : undefined,
            }}
          >
            {row.symbols.map((ch, c) => {
              const res = row.results[c] ?? 'empty';
              const st = tileColors(res);
              const atCursor = row.current === true && c === draft.length && draft.length < len;
              // A bought symbol stays legible under the cursor: mark the cell with
              // the pulsing border rather than replacing its glyph with a caret.
              const showCaret = atCursor && res !== 'hint';
              return (
                <div
                  key={c}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 'clamp(3px,.7cqmin,6px)',
                    fontFamily: "'JetBrains Mono',monospace",
                    fontWeight: 800,
                    fontSize: 'clamp(13px,2.8cqmin,26px)',
                    boxShadow: res === 'correct' ? '0 0 clamp(6px,1.6cqmin,16px) rgba(166,255,46,.4)' : undefined,
                    animation: atCursor ? 'whCur 1s ease-in-out infinite' : undefined,
                    ...st,
                  }}
                >
                  {showCaret ? (
                    <span style={{ width: 'clamp(2px,.5cqmin,3px)', height: 'clamp(14px,2.6cqmin,24px)', background: color.lime, animation: 'caret 1s steps(1,end) infinite' }} />
                  ) : (
                    ch.trim()
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* keyboard: digits, then operators */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(3px,.8cqmin,7px)' }}>
        {[DIGIT_ROW, OP_ROW].map((row, i) => (
          <div key={i} style={{ display: 'flex', gap: 'clamp(3px,.7cqmin,6px)' }}>
            {row.map((key) => {
              const wide = key.length > 1;
              const st = kbState.get(key);
              const bg =
                st === 'correct'
                  ? `linear-gradient(160deg,${color.limeBright},${color.limeDeep})`
                  : st === 'present'
                    ? 'linear-gradient(160deg,#ffc75a,#f0a000)'
                    : st === 'absent'
                      ? 'rgba(255,255,255,.03)'
                      : key === 'ENTER'
                        ? 'rgba(166,255,46,.16)'
                        : 'rgba(255,255,255,.07)';
              const fg =
                st === 'correct'
                  ? color.limeInk
                  : st === 'present'
                    ? color.amberInk
                    : st === 'absent'
                      ? 'rgba(234,255,234,.3)'
                      : key === 'ENTER'
                        ? color.limeBright
                        : color.text;
              return (
                <button
                  key={key}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    press(key);
                  }}
                  style={{
                    flex: wide ? 1.8 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 'clamp(30px,5.4cqmin,46px)',
                    borderRadius: 'clamp(3px,.9cqmin,7px)',
                    fontFamily: wide ? undefined : "'JetBrains Mono',monospace",
                    fontWeight: 700,
                    fontSize: wide ? 'clamp(9px,1.7cqmin,14px)' : 'clamp(13px,2.4cqmin,20px)',
                    letterSpacing: wide ? '.06em' : undefined,
                    background: bg,
                    color: fg,
                    touchAction: 'manipulation',
                  }}
                >
                  {key === 'BACK' ? '⌫' : key}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* why a guess bounced */}
      {shake > 0 && !server.isComplete && (
        <div style={{ textAlign: 'center', fontSize: 'clamp(9px,1.6cqmin,13px)', color: 'rgba(255,176,32,.85)', fontWeight: 600, letterSpacing: '.04em' }}>
          {Nerdle.RULES_HINT}
        </div>
      )}

      {/* answer is only ever present after the segment closes */}
      {server.isComplete && !server.solved && server.answer && (
        <div style={{ textAlign: 'center', fontSize: 'clamp(11px,2cqmin,16px)', color: color.redBright, fontWeight: 700, letterSpacing: '.06em' }}>
          ANSWER WAS <span style={{ fontFamily: "'JetBrains Mono',monospace", color: color.text }}>{server.answer}</span> · +60s
        </div>
      )}
    </div>
  );
}
